import React from "react";

export default class DownloadButton extends React.Component {
  state = {
    downloading: false
  };

  // Helper to detect mobile
  isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Helper to share/save video on mobile
  shareVideoMobile = async (blob, fileName) => {
    // Try Web Share API if available (works on iOS Safari, Android Chrome)
    if (navigator.share && navigator.canShare) {
      try {
        const file = new File([blob], fileName, { type: blob.type });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: 'Live Read Video',
            text: 'Check out my live read video!'
          });
          return true;
        }
      } catch (error) {
        // User cancelled or share failed, fall through to fallback
        if (error.name !== 'AbortError') {
          console.warn('Share API failed:', error);
        }
      }
    }
    
    // Fallback: Open in new tab (user can long-press to save on mobile)
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    
    // Clean up after a delay
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return true;
  };

  // Helper to download on desktop
  downloadVideoDesktop = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  handleDownload = async () => {
    const { recordedBlob, videoUrl, videoId, chapterTitle } = this.props;
    
    const fileNameBase = (chapterTitle || 'live-read')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    
    const isMobile = this.isMobile();

    // If we have a recorded blob, download it directly
    if (recordedBlob) {
      const extension = recordedBlob.fileExtension || 'webm';
      const fileName = `${fileNameBase}.${extension}`;

      if (isMobile) {
        await this.shareVideoMobile(recordedBlob, fileName);
      } else {
        this.downloadVideoDesktop(recordedBlob, fileName);
      }
      return;
    }

    // If we have a videoId, fetch download URL from backend (prefers converted)
    if (videoId) {
      try {
        this.setState({ downloading: true });
        
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/v1/worker/video/${videoId}/download-url`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) throw new Error('Failed to get download URL');
        
        const data = await response.json();
        const { downloadUrl, fileExtension } = data;

        // Fetch the video as a blob
        const videoResponse = await fetch(downloadUrl);
        if (!videoResponse.ok) throw new Error('Failed to fetch video');
        
        const blob = await videoResponse.blob();
        const fileName = `${fileNameBase}.${fileExtension}`;

        // Use appropriate method for mobile vs desktop
        if (isMobile) {
          await this.shareVideoMobile(blob, fileName);
        } else {
          this.downloadVideoDesktop(blob, fileName);
        }
        
        this.setState({ downloading: false });
      } catch (error) {
        console.error('Download failed:', error);
        this.setState({ downloading: false });
        alert('Failed to download video. Please try again.');
      }
      return;
    }

    // Fallback: If we only have a video URL (legacy)
    if (videoUrl) {
      try {
        this.setState({ downloading: true });
        
        const response = await fetch(videoUrl);
        if (!response.ok) throw new Error('Failed to fetch video');
        
        const blob = await response.blob();
        const urlExtension = videoUrl.match(/\.([^./?#]+)(?:[?#]|$)/)?.[1];
        const extension = urlExtension || 'mp4';
        const fileName = `${fileNameBase}.${extension}`;

        // Use appropriate method for mobile vs desktop
        if (isMobile) {
          await this.shareVideoMobile(blob, fileName);
        } else {
          this.downloadVideoDesktop(blob, fileName);
        }
        
        this.setState({ downloading: false });
      } catch (error) {
        console.error('Download failed:', error);
        this.setState({ downloading: false });
        alert('Failed to download video. Please try again.');
      }
    }
  };

  render() {
    const { className = "", style } = this.props;
    const { downloading } = this.state;

    return (
      <button 
        className={`lrp-btn lrp-btn-ghost ${className}`.trim()}
        style={style}
        onClick={this.handleDownload}
        disabled={downloading}
        title={downloading ? "Downloading..." : "Download recorded video"}
      >
        {downloading ? 'Downloading…' : 'Download'}
      </button>
    );
  }
}
